import type { ChangeEvent } from 'react';
import { cx } from './cx';

export interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Toggles error styling on the boxes; the error message itself is rendered by the caller (e.g. via `Field`). */
  error?: boolean;
  /** Source for the single real input's `aria-label`. */
  label?: string;
  className?: string;
}

/**
 * A single real `<input>` (so paste / WebOTP autofill work) drawn as `length` boxes.
 * The input sits on top of the boxes, sized to match, with transparent text/caret so the
 * boxed digits underneath are what's visible; the browser's own `:focus-visible` ring still
 * applies to the input itself.
 */
export const OtpInput = ({ length = 6, value, onChange, disabled, error, label, className = '' }: OtpInputProps) => {
  const boxes = Array.from({ length }, (_, i) => value[i] ?? '');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, length);
    onChange(digits);
  };

  return (
    <div className={cx('relative inline-flex gap-2', className)}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={handleChange}
        maxLength={length}
        className="absolute inset-0 h-full w-full rounded-sm border-none bg-transparent text-transparent caret-transparent disabled:cursor-not-allowed"
      />
      {boxes.map((char, i) => (
        <div
          key={i}
          aria-hidden="true"
          className={cx(
            'flex min-h-tap w-11 items-center justify-center rounded-sm border bg-white text-h3 text-ink',
            error ? 'border-alert' : 'border-sage-300',
          )}
        >
          {char}
        </div>
      ))}
    </div>
  );
};
